import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectSenderAudioActive,
    selectSenderAudioStream,
} from 'flow/selectors/meeting-selectors';
import {
    broadcastOwnSpeaking,
    sendUserActive,
} from 'flow/actions/meeting-actions';
import { createMicSpeechDetector } from 'flow/utils/mic-speaking-utils';
import {
    createSpeakingDetector,
    SpeakingDetector,
} from 'flow/utils/speaking-detector';

// Mic sampling cadence. The detector flips on roughly the 2nd above-threshold
// sample, so this sets the onset-latency floor — 60ms keeps the highlight snappy.
// Cheap: the per-sample energy read is trivial and the harmonicity check is gated
// behind it. The detector measures elapsed wall-clock time, not tick count.
const SAMPLE_INTERVAL_MS = 60;
// Near-zero start gate so the highlight appears as soon as you speak — the
// Tier-2 harmonicity check (mic-speaking-utils) does the cough/click/typing
// rejection, so we don't need a long gate for that. The stop gate is a ~1s hold
// so brief pauses between words/sentences don't flicker the highlight off.
const SPEAK_START_MS = 0;
const SPEAK_STOP_MS = 1000;
// While speaking, re-assert state on this cadence: it keeps us in the backend's
// speaking set even if a later 'stop' is lost (must stay below the backend TTL),
// and paces the legacy `userActive` grid-ordering heartbeat (historically 1s).
const SPEAK_KEEPALIVE_MS = 1000;

/**
 * Detects when the *own* user is speaking from their microphone and reports the
 * start/stop transitions — plus a keepalive while speaking — to the backend via
 * broadcastOwnSpeaking. It also drives the legacy `userActive` heartbeat (which
 * still feeds grid positioning) on the same keepalive, so the now-removed
 * self-view mic-bars indicator no longer needs to. A pure side-effect hook with
 * no rendering; mount it once
 * somewhere that lives for the whole active meeting. Sampling is gated on the
 * mic being unmuted; muting, a stream swap, or unmount ends any in-progress
 * utterance with a stop.
 */
const useOwnSpeakingDetection = (): void => {
    const dispatch = useAppDispatch();
    const audioStream = useAppSelector(selectSenderAudioStream);
    const audioActive = useAppSelector(selectSenderAudioActive);
    // Lazily created once and kept for the hook's lifetime: passing the detector
    // as useRef's argument would re-run createSpeakingDetector on every render
    // (only the first result is ever kept), allocating a throwaway each time.
    const detectorRef = useRef<SpeakingDetector | null>(null);
    if (detectorRef.current === null) {
        detectorRef.current = createSpeakingDetector({
            startMs: SPEAK_START_MS,
            stopMs: SPEAK_STOP_MS,
        });
    }

    useEffect(() => {
        // detector is always set during render above; the `!detector` operand
        // only narrows the nullable ref type for TypeScript — the branch is never
        // actually taken.
        const detector = detectorRef.current;
        if (!audioStream || !audioActive || !detector) {
            return undefined;
        }

        const audioContext = new AudioContext();
        if (audioContext.state === 'suspended') {
            // Closing the context below while this is still settling rejects
            // it ("Closed before resume completed" in Firefox, on leaving a
            // meeting). `void` satisfies the linter but attaches no handler,
            // so the rejection surfaced as an uncaught one.
            audioContext.resume().catch(() => undefined);
        }
        const speechDetector = createMicSpeechDetector(
            audioContext,
            audioStream,
        );

        let keepalive: ReturnType<typeof setInterval> | undefined;
        const stopKeepalive = () => {
            if (keepalive !== undefined) {
                clearInterval(keepalive);
                keepalive = undefined;
            }
        };

        const interval = setInterval(() => {
            // Feed the current speaking verdict into the frame classifier so its
            // noise floor stays frozen for the whole turn (not just on voiced
            // frames) — otherwise the unvoiced gaps of continuous speech ratchet
            // the floor up and the highlight drops mid-sentence. One-frame lag
            // (last verdict) is immaterial at the 60ms cadence.
            const frameIsVoiced = speechDetector.sample(detector.isSpeaking());
            const edge = detector.sample(frameIsVoiced, performance.now());
            if (edge === 'start') {
                dispatch(broadcastOwnSpeaking(true));
                // Also drive the legacy `userActive`/`activityList` heartbeat.
                // Since VID-219 this client orders its own grid off
                // `speakingMembers`, so the heartbeat no longer feeds our
                // positioning — it's kept only for legacy clients that still
                // order off it, to be retired once they age out.
                dispatch(sendUserActive());
                // Re-assert both signals on the keepalive cadence while speaking:
                // the `userSpeaking` keepalive keeps us in the backend's speaking
                // set (TTL) even if the 'stop' below is lost, and `sendUserActive`
                // keeps the legacy heartbeat alive for legacy clients.
                keepalive = setInterval(() => {
                    dispatch(broadcastOwnSpeaking(true));
                    dispatch(sendUserActive());
                }, SPEAK_KEEPALIVE_MS);
            } else if (edge === 'stop') {
                stopKeepalive();
                dispatch(broadcastOwnSpeaking(false));
            }
        }, SAMPLE_INTERVAL_MS);

        return () => {
            clearInterval(interval);
            stopKeepalive();
            speechDetector.disconnect();
            // Same reason: closing an already-closed context rejects too.
            audioContext.close().catch(() => undefined);
            // Tearing down mid-utterance (mute, stream swap, leaving): retract the
            // active-speaker state if we'd asserted it, then always reset so the
            // next stream starts from silence with no pending hysteresis run (a
            // half-formed start/stop candidate) carried over on the persisted ref.
            if (detector.isSpeaking()) {
                dispatch(broadcastOwnSpeaking(false));
            }
            detector.reset();
        };
    }, [audioStream, audioActive, dispatch]);
};

export default useOwnSpeakingDetection;
